// Adopt a signature (or initials): use the saved one, type it in a handwriting font, draw it, or upload an image.
// Produces a trimmed PNG data URL, which is the only thing a signer uploads (the server checks it's a real PNG
// under 300 KB). Uploaded photos/scans get their light background made transparent in the browser.
import { useEffect, useRef, useState } from "react";
import { Eraser, ImageUp } from "lucide-react";
import { Button, cx, Modal } from "./ui";

const INK = "#1f2a6b";

type Mode = "saved" | "type" | "draw" | "upload";
const MODE_LABEL: Record<Mode, string> = { saved: "Saved", type: "Type", draw: "Draw", upload: "Upload" };

export function SignaturePad({
  kind,
  name,
  saved,
  canSave,
  title,
  adoptLabel = "Adopt and sign",
  onAdopt,
  onClose,
}: {
  kind: "signature" | "initials";
  name: string;
  /** The staff member's saved image (data URL), offered first. */
  saved?: string;
  /** Show "Save for next time" (members who signed in). */
  canSave?: boolean;
  title?: string;
  adoptLabel?: string;
  onAdopt: (png: string, save: boolean) => void;
  onClose: () => void;
}) {
  const initials = name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase())
    .join("")
    .slice(0, 4);
  const [mode, setMode] = useState<Mode>(saved ? "saved" : "type");
  const [uploaded, setUploaded] = useState<string | null>(null);
  const [save, setSave] = useState(!saved);
  const [text, setText] = useState(kind === "initials" ? initials : name);
  const [drawn, setDrawn] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pad = useRef<HTMLCanvasElement>(null);
  const W = kind === "initials" ? 300 : 600;
  const H = 180;

  useEffect(() => {
    void document.fonts?.load(`64px Caveat`).catch(() => undefined);
  }, []);

  // Drawing
  useEffect(() => {
    const c = pad.current;
    if (mode !== "draw" || !c) return;
    const ctx = c.getContext("2d")!;
    ctx.lineWidth = 3.2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = INK;
    let down = false;
    let last: [number, number] | null = null;
    const pos = (e: PointerEvent): [number, number] => {
      const r = c.getBoundingClientRect();
      return [((e.clientX - r.left) / r.width) * W, ((e.clientY - r.top) / r.height) * H];
    };
    const start = (e: PointerEvent) => {
      down = true;
      try {
        c.setPointerCapture(e.pointerId);
      } catch {
        /* pointer already gone; drawing still works without capture */
      }
      last = pos(e);
      e.preventDefault();
    };
    const move = (e: PointerEvent) => {
      if (!down || !last) return;
      const p = pos(e);
      ctx.beginPath();
      ctx.moveTo(...last);
      ctx.lineTo(...p);
      ctx.stroke();
      last = p;
      setDrawn(true);
    };
    const end = () => {
      down = false;
      last = null;
    };
    c.addEventListener("pointerdown", start);
    c.addEventListener("pointermove", move);
    c.addEventListener("pointerup", end);
    c.addEventListener("pointercancel", end);
    return () => {
      c.removeEventListener("pointerdown", start);
      c.removeEventListener("pointermove", move);
      c.removeEventListener("pointerup", end);
      c.removeEventListener("pointercancel", end);
    };
  }, [mode, W]);

  const clear = () => {
    pad.current?.getContext("2d")?.clearRect(0, 0, W, H);
    setDrawn(false);
  };

  const adopt = async () => {
    setError(null);
    let source: HTMLCanvasElement;
    if (mode === "type") {
      if (!text.trim()) return setError(kind === "initials" ? "Type your initials" : "Type your name");
      await document.fonts?.load(`72px Caveat`).catch(() => undefined);
      source = document.createElement("canvas");
      source.width = W * 2;
      source.height = H;
      const ctx = source.getContext("2d")!;
      let size = 96;
      ctx.font = `700 ${size}px Caveat, "Segoe Script", cursive`;
      while (ctx.measureText(text).width > source.width - 40 && size > 30) {
        size -= 4;
        ctx.font = `700 ${size}px Caveat, "Segoe Script", cursive`;
      }
      ctx.fillStyle = INK;
      ctx.textBaseline = "middle";
      ctx.fillText(text.trim(), 20, H / 2);
    } else if (mode === "draw") {
      if (!drawn || !pad.current) return setError("Draw your " + (kind === "initials" ? "initials" : "signature"));
      source = pad.current;
    } else if (mode === "upload") {
      if (!uploaded) return setError("Choose an image of your " + (kind === "initials" ? "initials" : "signature"));
      return onAdopt(uploaded, Boolean(canSave && save));
    } else {
      return onAdopt(saved!, false);
    }
    const png = trim(source);
    if (!png) return setError("That came out empty. Try again.");
    onAdopt(png, Boolean(canSave && save));
  };

  return (
    <Modal
      title={title ?? (kind === "initials" ? "Adopt your initials" : "Adopt your signature")}
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={adopt}>
            {adoptLabel}
          </Button>
        </>
      }
    >
      <div className="flex gap-1 rounded-lg bg-surface-2 p-1 w-fit mb-4" role="tablist">
        {((saved ? ["saved", "type", "draw", "upload"] : ["type", "draw", "upload"]) as Mode[]).map((m) => (
          <button key={m} role="tab" aria-selected={mode === m} onClick={() => (setMode(m), setError(null))} className={cx("px-3 h-8 rounded-md text-sm font-medium", mode === m ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink")}>
            {MODE_LABEL[m]}
          </button>
        ))}
      </div>
      {mode === "saved" ? (
        <>
          <div className="rounded-lg border border-line bg-white h-28 flex items-center justify-center px-5 overflow-hidden">
            <img src={saved} alt={`Your saved ${kind}`} className="max-h-24 max-w-full object-contain" />
          </div>
          <p className="text-xs text-muted mt-2">Your saved {kind}. Choose Type, Draw or Upload to use a different one.</p>
        </>
      ) : mode === "upload" ? (
        <>
          <label className="rounded-lg border border-dashed border-line bg-white h-28 flex items-center justify-center px-5 overflow-hidden cursor-pointer hover:border-accent">
            {uploaded ? (
              <img src={uploaded} alt={`Uploaded ${kind}`} className="max-h-24 max-w-full object-contain" />
            ) : (
              <span className="text-sm text-muted flex items-center gap-2">
                <ImageUp className="size-5" /> Choose a PNG or JPG of your {kind}
              </span>
            )}
            <input
              type="file"
              accept="image/png,image/jpeg"
              className="sr-only"
              onChange={async (e) => {
                const file = e.target.files?.[0];
                e.target.value = "";
                if (!file) return;
                setError(null);
                try {
                  setUploaded(await fromImage(file, kind === "initials" ? 600 : 1200, 400));
                } catch (err) {
                  setUploaded(null);
                  setError((err as Error).message);
                }
              }}
            />
          </label>
          <p className="text-xs text-muted mt-2">A photo or scan of your signature on white paper works best; the background is removed automatically.</p>
        </>
      ) : mode === "type" ? (
        <>
          <label className="block text-sm font-medium text-ink-2 mb-1" htmlFor="sig-text">
            {kind === "initials" ? "Initials" : "Full name"}
          </label>
          <input id="sig-text" value={text} maxLength={kind === "initials" ? 6 : 80} onChange={(e) => setText(e.target.value)} className="w-full h-10 rounded-lg border border-line bg-surface px-3 text-sm text-ink" />
          <div className="mt-4 rounded-lg border border-line bg-white h-28 flex items-center px-5 overflow-hidden">
            <span className="signature-font text-5xl font-bold truncate" style={{ color: INK }}>
              {text || " "}
            </span>
          </div>
        </>
      ) : (
        <>
          <div className="relative rounded-lg border border-line bg-white">
            <canvas ref={pad} width={W} height={H} className="w-full touch-none cursor-crosshair" style={{ aspectRatio: `${W} / ${H}` }} aria-label="Draw here" />
            <div className="absolute left-5 right-5 bottom-8 border-b border-dashed border-black/20 pointer-events-none" />
            <Button variant="ghost" className="absolute top-2 right-2 h-8" onClick={clear}>
              <Eraser className="size-4" /> Clear
            </Button>
          </div>
          <p className="text-xs text-muted mt-2">Draw with your mouse, finger or pen.</p>
        </>
      )}
      {error && <p className="text-sm text-bad mt-3">{error}</p>}
      {canSave && mode !== "saved" && (
        <label className="flex items-center gap-2 text-sm text-ink-2 mt-4">
          <input type="checkbox" checked={save} onChange={(e) => setSave(e.target.checked)} className="size-4" />
          Save {saved ? "as my new" : "this as my"} {kind} for next time
        </label>
      )}
      <p className="text-xs text-muted mt-4">
        {adoptLabel === "Adopt and sign"
          ? `By selecting Adopt and sign, I agree that this mark is my electronic ${kind} and has the same effect as my handwritten one on this document.`
          : `Saved to your eSignIt account. Each time you use it to sign, you agree it is your electronic ${kind} for that document.`}
      </p>
    </Modal>
  );
}

/**
 * An uploaded photo or scan to a trimmed PNG with a transparent background: scaled to fit maxW x maxH, light pixels
 * (paper) made transparent and the rest kept as ink. Shrinks further if the PNG would exceed the 300 KB limit.
 */
async function fromImage(file: File, maxW: number, maxH: number): Promise<string> {
  if (!/^image\/(png|jpeg)$/.test(file.type)) throw new Error("Choose a PNG or JPG image");
  if (file.size > 15 * 1024 * 1024) throw new Error("That image is too large (over 15 MB)");
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((ok, bad) => {
      const i = new Image();
      i.onload = () => ok(i);
      i.onerror = () => bad(new Error("That image couldn't be read"));
      i.src = url;
    });
    for (let scale = Math.min(1, maxW / img.naturalWidth, maxH / img.naturalHeight); scale > 0.05; scale *= 0.75) {
      const c = document.createElement("canvas");
      c.width = Math.max(1, Math.round(img.naturalWidth * scale));
      c.height = Math.max(1, Math.round(img.naturalHeight * scale));
      const ctx = c.getContext("2d")!;
      ctx.drawImage(img, 0, 0, c.width, c.height);
      const px = ctx.getImageData(0, 0, c.width, c.height);
      const d = px.data;
      for (let i = 0; i < d.length; i += 4) {
        const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        // Paper becomes transparent; a soft ramp keeps pen edges smooth.
        const a = lum >= 215 ? 0 : lum <= 150 ? 255 : Math.round(((215 - lum) / 65) * 255);
        d[i + 3] = Math.min(d[i + 3], a);
      }
      ctx.putImageData(px, 0, 0);
      const png = trim(c);
      if (!png) throw new Error("No signature found in that image. Use dark ink on light paper.");
      if (png.length * 0.75 < 290 * 1024) return png;
    }
    throw new Error("That image is too detailed. Crop it to just the signature and try again.");
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Crops transparent margins (keeping a little padding) and returns a PNG data URL, or null if empty. */
function trim(src: HTMLCanvasElement): string | null {
  const ctx = src.getContext("2d")!;
  const { width, height } = src;
  const data = ctx.getImageData(0, 0, width, height).data;
  let x0 = width,
    y0 = height,
    x1 = -1,
    y1 = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  const pad = 6;
  x0 = Math.max(0, x0 - pad);
  y0 = Math.max(0, y0 - pad);
  x1 = Math.min(width - 1, x1 + pad);
  y1 = Math.min(height - 1, y1 + pad);
  const out = document.createElement("canvas");
  out.width = x1 - x0 + 1;
  out.height = y1 - y0 + 1;
  out.getContext("2d")!.drawImage(src, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out.toDataURL("image/png");
}
